import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import * as Highcharts from 'highcharts';
import HighchartsSankey from 'highcharts/modules/sankey';
import {
  IActivo,
  IEmergencia,
  IPersonalSalud,
  IPopulate,
  IQueryParam,
  IUbicacion,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import {
  GraficosComponent,
  IBarChartData,
  ILineChartData,
  IPieChartData,
} from '../../../../../auxiliares/graficos/graficos.component';
import {
  IColumnas,
  TablaComponent,
} from '../../../../../auxiliares/tabla/tabla.component';
import {
  StatCardComponent,
  StatCardConfig,
} from '../../../../../componentes/stat-card/stat-card.component';
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../../../componentes/date-picker/date-picker.component';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import { DashboardFiltrosComponent } from '../../../../../componentes/dashboard-filtros/dashboard-filtros.component';
import {
  DashboardGrillaComponent,
  DashboardGrillaItem,
} from '../../../../../componentes/dashboard-grilla/dashboard-grilla.component';
import { DashboardPanelComponent } from '../../../../../componentes/dashboard-panel/dashboard-panel.component';
import {
  ESTADOS_EMERGENCIA,
  ESTADOS_FINALES,
  PRIORIDADES,
  SituacionEmergencia,
  estadoCeldaEmergencia,
  estadoDe,
  prioridadCelda,
  situacionCelda,
  presentacionEstado,
  presentacionPrioridad,
  presentacionSituacion,
  situacionDe,
} from '../../estado-emergencia';

// El sankey no viene en el core de Highcharts (app-graficos usa 'highcharts').
HighchartsSankey(Highcharts);

type TipoGraficoTablero =
  | 'pie'
  | 'donut'
  | 'bar'
  | 'column'
  | 'line'
  | 'sankey'
  | 'tabla-activas'
  | 'tabla-tiempos'
  | 'mapa';

interface GraficoConfig extends DashboardGrillaItem {
  titulo: string;
  /** Ícono del encabezado del panel */
  icono: string;
  /** Mensaje cuando el panel no tiene datos para mostrar */
  sinDatos: string;
  tipo: TipoGraficoTablero;
  altura: string;
  /** Qué consulta tiene que terminar para poder dibujarlo. */
  fuente: 'activas' | 'historicas' | 'historicasYEntidades';
}

interface FilaTiempos {
  prioridad: string;
  cantidad: number;
  hastaAsignacion: number | null;
  hastaLlegada: number | null;
  hastaFinalizacion: number | null;
  reasignaciones: number;
  llegaronHospital: number;
}

const SITUACIONES: SituacionEmergencia[] = [
  'Auxilio',
  'Seguimiento',
  'Registrada finalizada',
  'Llamada',
  'Importada',
];

const DIA_MS = 24 * 60 * 60 * 1000;
/** Hasta este rango el histórico se agrupa por día; más largo, por mes. */
const MAX_DIAS_AGRUPAR_POR_DIA = 62;

function numero(valor: number, decimales = 0): string {
  return valor.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  });
}

/** Minutos → "12,5 min" o "2 h 05 min". */
function duracion(min: number | null): string | null {
  if (min === null || !isFinite(min)) return null;
  if (min < 60) return `${numero(min, 1)} min`;
  const horas = Math.floor(min / 60);
  const resto = Math.round(min % 60);
  return `${numero(horas)} h ${String(resto).padStart(2, '0')} min`;
}

function promedio(valores: number[]): number | null {
  if (!valores.length) return null;
  return valores.reduce((a, b) => a + b, 0) / valores.length;
}

function minutosEntre(desde?: string, hasta?: string): number | null {
  if (!desde || !hasta) return null;
  const d = new Date(hasta).getTime() - new Date(desde).getTime();
  return isFinite(d) && d >= 0 ? d / 60000 : null;
}

import { htmlDireccion, tooltipDireccion } from '../presentacion-entidades';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
@Component({
  selector: 'app-tablero-emergencias-medicas',
  templateUrl: './tablero-emergencias-medicas.component.html',
  styleUrl: './tablero-emergencias-medicas.component.scss',
  imports: [
    TooltipDirective,
    FormsModule,
    BOTONES,
    MatIconModule,
    MatProgressSpinnerModule,

    StatCardComponent,
    GraficosComponent,
    TablaComponent,
    DatePickerComponent,
    MapCardComponent,
    DashboardFiltrosComponent,
    DashboardGrillaComponent,
    DashboardPanelComponent,
  ],
})
export class TableroEmergenciasMedicasComponent implements OnInit, OnDestroy {
  private helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private emergenciasService = inject(EmergenciasService);

  // Datos crudos
  public activas: IEmergencia[] = [];
  public historicas: IEmergencia[] = [];
  private ambulancias: IActivo[] = [];
  private personal: IPersonalSalud[] = [];
  private hospitales: IUbicacion[] = [];

  private activas$?: Subscription;
  private ambulancias$?: Subscription;
  private personal$?: Subscription;
  private hospitales$?: Subscription;

  // Carga
  public cargando = {
    activas: true,
    historicas: true,
    ambulancias: true,
    personal: true,
    hospitales: true,
  };

  // Período del histórico (por defecto, los últimos 30 días)
  public hoy = new Date();
  public periodo: DateRangeValue = (() => {
    const fin = new Date();
    const inicio = new Date(fin.getTime() - 29 * DIA_MS);
    inicio.setHours(0, 0, 0, 0);
    return { start: inicio.toISOString(), end: fin.toISOString() };
  })();

  // Stat cards
  public statCards: StatCardConfig[] = [];

  // Grilla
  public graficos: GraficoConfig[] = [];

  // Datos de los gráficos
  public datosActivasCodigo: IPieChartData[] = [];
  public datosActivasEstado: IPieChartData[] = [];
  public datosHistoricasSituacion: IPieChartData[] = [];
  public datosHistoricasEstado: IPieChartData[] = [];
  public datosCierreCodigo: IPieChartData[] = [];
  public seriesPorFecha: ILineChartData[] = [];
  public categoriasPorFecha: string[] = [];
  public tituloPorFecha = 'Emergencias por día';
  public seriesTiempos: IBarChartData[] = [];
  public categoriasTiempos: string[] = [];
  public filasTiempos: FilaTiempos[] = [];
  public opcionesSankey: Partial<Highcharts.Options> = {};
  public versionSankey = 0;
  public haySankey = false;
  public rankings: Record<
    'rankingAmbulancias' | 'rankingPersonal' | 'rankingHospitales',
    { series: IBarChartData[]; categorias: string[] }
  > = {
    rankingAmbulancias: { series: [], categorias: [] },
    rankingPersonal: { series: [], categorias: [] },
    rankingHospitales: { series: [], categorias: [] },
  };
  public mapaHistorico?: MapCardConfig;
  public subtituloMapa = '';

  // Tablas embebidas
  public columnasActivas: IColumnas<IEmergencia>[] = [
    {
      header: { label: 'Código', sort: 'codigo' },
      row: {
        links: (e) => [
          {
            link: e.codigo ? `#${e.codigo}` : 'Ver detalle',
            tooltip: 'Ver detalle',
            route: ['/main/emergencias/emergenciasMedicas/detalles', e._id],
          },
        ],
      },
    },
    {
      // Mismo nombre que en el listado de emergencias (antes «Triage»)
      header: { label: 'Código de apertura' },
      row: { estado: (e) => prioridadCelda(e.prioridadApertura) },
    },
    {
      header: { label: 'Estado' },
      row: { estado: (e) => estadoCeldaEmergencia(estadoDe(e)) },
    },
    {
      header: { label: 'Situación' },
      row: { estado: (e) => situacionCelda(situacionDe(e)) },
    },
    {
      header: { label: 'Dirección y paciente' },
      row: {
        html: (e) => {
          const direccion = htmlDireccion(e.direccion);
          const p = e.destinatarioAsistencia;
          const nombre = [p?.nombre, p?.apellido].filter(Boolean).join(' ');
          const paciente = nombre
            ? `<span class="tabla-secundario">${escaparHtml(nombre)}</span>`
            : '<span class="tabla-secundario no-info">Sin paciente</span>';
          return `<div class="tabla-celda-col">${direccion}${paciente}</div>`;
        },
        tooltip: (e) => tooltipDireccion(e.direccion),
      },
    },
    {
      header: { label: 'Creada', sort: 'fechaCreacion' },
      row: {
        fecha: (e) => e.fechaCreacion,
        hace: true,
        noData: 'Sin fecha',
      },
    },
  ];

  public columnasTiempos: IColumnas<FilaTiempos>[] = [
    {
      header: { label: 'Código' },
      row: { estado: (f) => prioridadCelda(f.prioridad) },
    },
    {
      header: { label: 'Cantidad', sort: 'cantidad' },
      row: { parse: (f) => numero(f.cantidad) },
    },
    {
      header: { label: 'Hasta asignación' },
      row: {
        parse: (f) => duracion(f.hastaAsignacion) ?? undefined,
        noData: 'Sin datos',
      },
    },
    {
      header: { label: 'Hasta llegada' },
      row: {
        parse: (f) => duracion(f.hastaLlegada) ?? undefined,
        noData: 'Sin datos',
      },
    },
    {
      header: { label: 'Hasta finalización' },
      row: {
        parse: (f) => duracion(f.hastaFinalizacion) ?? undefined,
        noData: 'Sin datos',
      },
    },
    {
      header: { label: 'Reasignaciones (promedio)' },
      row: { parse: (f) => numero(f.reasignaciones, 2) },
    },
    {
      header: { label: 'Llegaron a hospital' },
      row: { parse: (f) => numero(f.llegaronHospital) },
    },
  ];

  // ---------------------------------------------------------------------------
  // Grilla

  private inicializarGraficos(): void {
    this.graficos = [
      {
        id: 'activasCodigo',
        icono: 'emergency',
        sinDatos: 'No hay emergencias activas',
        titulo: 'Activas por código',
        tipo: 'pie',
        altura: '320px',
        fuente: 'activas',
      },
      {
        id: 'activasEstado',
        icono: 'pie_chart',
        sinDatos: 'No hay emergencias activas',
        titulo: 'Activas por estado',
        tipo: 'pie',
        altura: '320px',
        fuente: 'activas',
      },
      {
        id: 'listadoActivas',
        icono: 'list',
        sinDatos: 'No hay emergencias activas',
        ancho: 'ancho',
        titulo: 'Emergencias activas',
        tipo: 'tabla-activas',
        altura: '360px',
        fuente: 'activas',
      },
      {
        id: 'porFecha',
        icono: 'show_chart',
        sinDatos: 'No hay emergencias históricas en el período',
        titulo: 'Emergencias por fecha',
        tipo: 'line',
        altura: '320px',
        fuente: 'historicas',
      },
      {
        id: 'historicasSituacion',
        icono: 'donut_large',
        sinDatos: 'No hay emergencias históricas en el período',
        titulo: 'Históricas por situación',
        tipo: 'donut',
        altura: '320px',
        fuente: 'historicas',
      },
      {
        id: 'historicasEstado',
        icono: 'task_alt',
        sinDatos: 'No hay emergencias históricas en el período',
        titulo: 'Históricas: finalizadas y canceladas',
        tipo: 'donut',
        altura: '320px',
        fuente: 'historicas',
      },
      {
        id: 'cierreCodigo',
        icono: 'pie_chart',
        sinDatos: 'No hay emergencias históricas en el período',
        titulo: 'Por código de cierre',
        tipo: 'pie',
        altura: '320px',
        fuente: 'historicas',
      },
      {
        id: 'tiemposGrafico',
        icono: 'bar_chart',
        sinDatos:
          'No hay tiempos de atención de emergencias finalizadas en el período',
        titulo: 'Tiempos promedio por código (minutos)',
        tipo: 'column',
        altura: '320px',
        fuente: 'historicas',
      },
      {
        id: 'tiemposTabla',
        icono: 'timer',
        sinDatos: 'No hay emergencias finalizadas en el período',
        ancho: 'ancho',
        titulo: 'Tiempos de atención por código',
        tipo: 'tabla-tiempos',
        altura: '360px',
        fuente: 'historicas',
      },
      {
        id: 'sankey',
        icono: 'account_tree',
        sinDatos: 'No hay emergencias finalizadas en el período',
        titulo: 'Código de apertura → código de cierre',
        tipo: 'sankey',
        altura: '360px',
        fuente: 'historicas',
      },
      {
        id: 'rankingAmbulancias',
        icono: 'airport_shuttle',
        sinDatos: 'No hay ambulancias asignadas a emergencias en el período',
        titulo: 'Ambulancias con más emergencias',
        tipo: 'bar',
        altura: '320px',
        fuente: 'historicasYEntidades',
      },
      {
        id: 'rankingPersonal',
        icono: 'medical_services',
        sinDatos:
          'No hay personal de salud asignado a emergencias en el período',
        titulo: 'Personal de salud con más emergencias',
        tipo: 'bar',
        altura: '320px',
        fuente: 'historicasYEntidades',
      },
      {
        id: 'rankingHospitales',
        icono: 'local_hospital',
        sinDatos: 'No hay emergencias con hospital de destino en el período',
        titulo: 'Hospitales de destino',
        tipo: 'bar',
        altura: '320px',
        fuente: 'historicasYEntidades',
      },
      {
        id: 'mapaHistorico',
        icono: 'map',
        sinDatos: 'No hay auxilios con ubicación en el período',
        titulo: 'Mapa de calor de auxilios',
        tipo: 'mapa',
        altura: '360px',
        fuente: 'historicas',
      },
    ];
  }

  public graficoListo(g: GraficoConfig): boolean {
    switch (g.fuente) {
      case 'activas':
        return !this.cargando.activas;
      case 'historicas':
        return !this.cargando.historicas;
      case 'historicasYEntidades':
        return (
          !this.cargando.historicas &&
          !this.cargando.ambulancias &&
          !this.cargando.personal &&
          !this.cargando.hospitales
        );
    }
  }

  /**
   * Si el panel tiene algo que mostrar. Sin datos se muestra el mensaje del
   * panel en lugar de un gráfico vacío. En las tortas toda porción es una
   * emergencia real (también "Sin código"), así que cualquier valor cuenta.
   */
  public tieneDatos(g: GraficoConfig): boolean {
    const hayValor = (series: (IBarChartData | ILineChartData)[]) =>
      series.some((serie) =>
        (serie.data as (number | [number, number])[]).some(
          (v) => (Array.isArray(v) ? v[1] : v) > 0,
        ),
      );
    switch (g.tipo) {
      case 'pie':
      case 'donut':
        return this.datosPie(g.id).some((d) => d.y > 0);
      case 'line':
        return hayValor(this.seriesPorFecha);
      case 'column':
        return hayValor(this.seriesTiempos);
      case 'bar':
        return hayValor(this.ranking(g.id).series);
      case 'sankey':
        return this.haySankey;
      case 'tabla-activas':
        return this.activas.length > 0;
      case 'tabla-tiempos':
        return this.filasTiempos.length > 0;
      case 'mapa':
        return !!this.mapaHistorico;
    }
  }

  /** Título del panel; el de fechas cambia según cómo se agrupa el período. */
  public tituloPanel(g: GraficoConfig): string {
    return g.id === 'porFecha' ? this.tituloPorFecha : g.titulo;
  }

  public datosPie(id: string): IPieChartData[] {
    switch (id) {
      case 'activasCodigo':
        return this.datosActivasCodigo;
      case 'activasEstado':
        return this.datosActivasEstado;
      case 'historicasSituacion':
        return this.datosHistoricasSituacion;
      case 'historicasEstado':
        return this.datosHistoricasEstado;
      case 'cierreCodigo':
        return this.datosCierreCodigo;
      default:
        return [];
    }
  }

  public ranking(id: string): {
    series: IBarChartData[];
    categorias: string[];
  } {
    return (
      this.rankings[id as keyof typeof this.rankings] ?? {
        series: [],
        categorias: [],
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Consultas

  private listarActivas(): void {
    // Mismo criterio que los listados de activas
    const filter: Record<string, unknown> = {
      tipo: 'Médica',
      'ultimoEventoEmergencia.estado': { $nin: ESTADOS_FINALES },
      importada: { $ne: true },
    };
    const populate: IPopulate[] = [
      { path: 'destinatarioAsistencia', select: 'nombre apellido' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      sort: '-fechaCreacion',
      unlimited: true,
      includeChildren: true,
    };
    this.activas$?.unsubscribe();
    this.activas$ = this.lista2.emergencias(query, 15).subscribe({
      next: (data) => {
        if (
          !this.cargando.activas &&
          !HelperService.cambiaronLosDatos(this.activas, data.datos)
        ) {
          return;
        }
        this.activas = data.datos;
        this.procesarActivas();
        this.cargando.activas = false;
        this.actualizarStatCards();
      },
      error: () => {
        this.cargando.activas = false;
        this.helper.notifError('No se pudieron cargar las emergencias activas');
      },
    });
  }

  public async listarHistoricas(): Promise<void> {
    this.cargando.historicas = true;
    const filter: Record<string, unknown> = {
      tipo: 'Médica',
      'ultimoEventoEmergencia.estado': { $in: ESTADOS_FINALES },
    };
    if (this.periodo?.start && this.periodo?.end) {
      const hasta = new Date(this.periodo.end);
      hasta.setHours(23, 59, 59, 999);
      filter['fechaCreacion'] = {
        $gte: new Date(this.periodo.start).toISOString(),
        $lte: hasta.toISOString(),
      };
    }
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select:
        'codigo prioridadApertura prioridadCierre fechaCreacion fechaPrimeraAsignacion fechaLlegadaDestino fechaFinalizacion cantidadReasignaciones esAuxilio enSeguimiento iniciaFinalizada importada ubicacionDestino ultimoEventoEmergencia emergenciaMedica',
      sort: '-fechaCreacion',
      unlimited: true,
      includeChildren: true,
    };
    try {
      const res = await this.emergenciasService.getFiltered(query);
      this.historicas = res.datos ?? [];
    } catch {
      this.historicas = [];
      this.helper.notifError(
        'No se pudieron cargar las emergencias históricas',
      );
    }
    this.procesarHistoricas();
    this.procesarRankings();
    this.cargando.historicas = false;
  }

  private listarEntidades(): void {
    const ambulancias: IQueryParam = {
      filter: JSON.stringify({ funcion: 'Ambulancia' }),
      select: 'identificacion funcion',
      sort: 'identificacion',
      unlimited: true,
      includeChildren: true,
    };
    this.ambulancias$?.unsubscribe();
    this.ambulancias$ = this.lista2.vehiculos(ambulancias, 30).subscribe({
      next: (data) => {
        this.ambulancias = data.datos;
        this.cargando.ambulancias = false;
        this.actualizarStatCards();
        this.procesarRankings();
      },
      error: () => (this.cargando.ambulancias = false),
    });

    const personal: IQueryParam = {
      select: 'nombre rol activo',
      sort: 'nombre',
      unlimited: true,
      includeChildren: true,
    };
    this.personal$?.unsubscribe();
    this.personal$ = this.lista2.personalSaluds(personal, 30).subscribe({
      next: (data) => {
        this.personal = data.datos;
        this.cargando.personal = false;
        this.actualizarStatCards();
        this.procesarRankings();
      },
      error: () => (this.cargando.personal = false),
    });

    const hospitales: IQueryParam = {
      select: 'identificacion valores',
      sort: 'identificacion',
      unlimited: true,
      includeChildren: true,
    };
    this.hospitales$?.unsubscribe();
    this.hospitales$ = this.lista2.hospitals(hospitales, 30).subscribe({
      next: (data) => {
        this.hospitales = data.datos;
        this.cargando.hospitales = false;
        this.actualizarStatCards();
        this.procesarRankings();
      },
      error: () => (this.cargando.hospitales = false),
    });
  }

  public onPeriodoChange(valor: DateRangeValue): void {
    if (!valor?.start || !valor?.end) return;
    this.periodo = valor;
    this.listarHistoricas();
  }

  // ---------------------------------------------------------------------------
  // Procesamiento

  private actualizarStatCards(): void {
    const auxilios = this.activas.filter((e) => e.esAuxilio).length;
    const seguimiento = this.activas.filter((e) => e.enSeguimiento).length;
    const idsMovilesAsignados = new Set(
      this.activas
        .map(
          (e) => e.ultimoEventoEmergencia?.detallesEmergencias?.idMovilAsignado,
        )
        .filter(Boolean),
    );
    const personalActivo = this.personal.filter((p) => p.activo !== false);
    const medicos = personalActivo.filter((p) => p.rol === 'Médico').length;
    const enfermeros = personalActivo.filter(
      (p) => p.rol === 'Enfermero',
    ).length;
    const hospitalesActivos = this.hospitales.filter(
      (h) => (h.valores as { activo?: boolean } | undefined)?.activo !== false,
    ).length;

    this.statCards = [
      {
        title: 'Emergencias activas',
        value: this.activas.length,
        subtitle: 'Sin finalizar ni cancelar',
        icon: 'emergency',
        color: 'red',
      },
      {
        title: 'Auxilios activos',
        value: auxilios,
        subtitle: 'Con móvil en camino o por asignar',
        icon: 'emergency',
        color: 'orange',
      },
      {
        title: 'En seguimiento',
        value: seguimiento,
        subtitle: 'Sin móvil asignado',
        icon: 'monitor_heart',
        color: 'blue',
      },
      {
        title: 'Ambulancias',
        value: this.ambulancias.length,
        subtitle: `${numero(idsMovilesAsignados.size)} asignadas a emergencias activas`,
        icon: 'airport_shuttle',
        color: 'teal',
      },
      {
        title: 'Personal de salud activo',
        value: personalActivo.length,
        subtitle: `${numero(medicos)} médicos · ${numero(enfermeros)} enfermeros`,
        icon: 'medical_services',
        color: 'purple',
      },
      {
        title: 'Hospitales activos',
        value: hospitalesActivos,
        subtitle: `De ${numero(this.hospitales.length)} cargados`,
        icon: 'domain',
        color: 'green',
      },
    ];
  }

  private procesarActivas(): void {
    this.datosActivasCodigo = this.tortaPorPrioridad(
      this.activas,
      (e) => e.prioridadApertura,
    );
    this.datosActivasEstado = this.tortaPorEstado(this.activas);
  }

  private procesarHistoricas(): void {
    const hist = this.historicas;

    // Por situación
    const porSituacion = new Map<SituacionEmergencia, number>();
    for (const e of hist) {
      const s = situacionDe(e);
      porSituacion.set(s, (porSituacion.get(s) ?? 0) + 1);
    }
    this.datosHistoricasSituacion = SITUACIONES.filter((s) =>
      porSituacion.get(s),
    ).map((s) => ({
      name: presentacionSituacion(s).label,
      y: porSituacion.get(s)!,
      color: presentacionSituacion(s).color,
    }));

    this.datosHistoricasEstado = this.tortaPorEstado(hist);

    // Código de cierre (las canceladas cierran con el de apertura)
    this.datosCierreCodigo = this.tortaPorPrioridad(
      hist,
      (e) => e.prioridadCierre || e.prioridadApertura,
    );

    this.procesarPorFecha();
    this.procesarTiempos();
    this.procesarSankey();
    this.procesarMapa();
  }

  private tortaPorPrioridad(
    lista: IEmergencia[],
    fn: (e: IEmergencia) => string | undefined,
  ): IPieChartData[] {
    const conteo = new Map<string, number>();
    for (const e of lista) {
      const p = fn(e) || '';
      conteo.set(p, (conteo.get(p) ?? 0) + 1);
    }
    const claves = [
      ...PRIORIDADES.filter((p) => conteo.has(p)),
      ...[...conteo.keys()].filter(
        (k) => !PRIORIDADES.includes(k as (typeof PRIORIDADES)[number]),
      ),
    ];
    return claves.map((k) => ({
      name: presentacionPrioridad(k).label,
      y: conteo.get(k)!,
      color: presentacionPrioridad(k).color,
    }));
  }

  private tortaPorEstado(lista: IEmergencia[]): IPieChartData[] {
    const conteo = new Map<string, number>();
    for (const e of lista) {
      const est = estadoDe(e);
      conteo.set(est, (conteo.get(est) ?? 0) + 1);
    }
    const claves = [
      ...ESTADOS_EMERGENCIA.filter((s) => conteo.has(s)),
      ...[...conteo.keys()].filter(
        (k) =>
          !ESTADOS_EMERGENCIA.includes(
            k as (typeof ESTADOS_EMERGENCIA)[number],
          ),
      ),
    ];
    return claves.map((k) => ({
      name: presentacionEstado(k).label,
      y: conteo.get(k)!,
      color: presentacionEstado(k).color,
    }));
  }

  private procesarPorFecha(): void {
    const inicio = new Date(this.periodo.start);
    inicio.setHours(0, 0, 0, 0);
    const fin = new Date(this.periodo.end);
    fin.setHours(0, 0, 0, 0);
    const dias = Math.round((fin.getTime() - inicio.getTime()) / DIA_MS) + 1;
    const porDia = dias <= MAX_DIAS_AGRUPAR_POR_DIA;
    this.tituloPorFecha = porDia
      ? 'Emergencias por día'
      : 'Emergencias por mes';

    const pad = (n: number) => String(n).padStart(2, '0');
    const clave = (d: Date) =>
      porDia
        ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
        : `${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

    // Todas las franjas del período, aunque no tengan emergencias
    const categorias: string[] = [];
    const cursor = new Date(inicio);
    if (!porDia) cursor.setDate(1);
    while (cursor <= fin) {
      categorias.push(clave(cursor));
      if (porDia) cursor.setDate(cursor.getDate() + 1);
      else cursor.setMonth(cursor.getMonth() + 1);
    }
    const indice = new Map(categorias.map((c, i) => [c, i]));

    const conteo = new Map<SituacionEmergencia, number[]>();
    for (const e of this.historicas) {
      if (!e.fechaCreacion) continue;
      const i = indice.get(clave(new Date(e.fechaCreacion)));
      if (i === undefined) continue;
      const s = situacionDe(e);
      if (!conteo.has(s)) conteo.set(s, new Array(categorias.length).fill(0));
      conteo.get(s)![i]++;
    }

    this.categoriasPorFecha = categorias;
    this.seriesPorFecha = SITUACIONES.filter((s) => conteo.has(s)).map((s) => ({
      name: presentacionSituacion(s).label,
      data: conteo.get(s)!,
      color: presentacionSituacion(s).color,
    }));
  }

  private procesarTiempos(): void {
    const finalizadas = this.historicas.filter(
      (e) => estadoDe(e) === 'Finalizada',
    );
    const grupos = new Map<string, IEmergencia[]>();
    for (const e of finalizadas) {
      const p = e.prioridadApertura || '';
      if (!grupos.has(p)) grupos.set(p, []);
      grupos.get(p)!.push(e);
    }
    const claves = [
      ...PRIORIDADES.filter((p) => grupos.has(p)),
      ...[...grupos.keys()].filter(
        (k) => !PRIORIDADES.includes(k as (typeof PRIORIDADES)[number]),
      ),
    ];

    this.filasTiempos = claves.map((p) => {
      const lista = grupos.get(p)!;
      const tiempos = (fn: (e: IEmergencia) => number | null): number | null =>
        promedio(lista.map(fn).filter((v): v is number => v !== null));
      return {
        prioridad: p,
        cantidad: lista.length,
        hastaAsignacion: tiempos((e) =>
          minutosEntre(e.fechaCreacion, e.fechaPrimeraAsignacion),
        ),
        hastaLlegada: tiempos((e) =>
          minutosEntre(e.fechaCreacion, e.fechaLlegadaDestino),
        ),
        hastaFinalizacion: tiempos((e) =>
          minutosEntre(e.fechaCreacion, e.fechaFinalizacion),
        ),
        reasignaciones:
          promedio(lista.map((e) => e.cantidadReasignaciones || 0)) ?? 0,
        llegaronHospital: lista.filter(
          (e) => !!e.emergenciaMedica?.fechaLlegadaHospital,
        ).length,
      };
    });

    const redondear = (v: number | null) =>
      v === null ? 0 : Math.round(v * 10) / 10;
    this.categoriasTiempos = this.filasTiempos.map(
      (f) => presentacionPrioridad(f.prioridad).label,
    );
    this.seriesTiempos = [
      {
        name: 'Hasta asignación',
        data: this.filasTiempos.map((f) => redondear(f.hastaAsignacion)),
        color: presentacionEstado('Asignada').color,
      },
      {
        name: 'Hasta llegada',
        data: this.filasTiempos.map((f) => redondear(f.hastaLlegada)),
        color: presentacionEstado('Llegó a destino').color,
      },
      {
        name: 'Hasta finalización',
        data: this.filasTiempos.map((f) => redondear(f.hastaFinalizacion)),
        color: presentacionEstado('Finalizada').color,
      },
    ];
  }

  private procesarSankey(): void {
    const finalizadas = this.historicas.filter(
      (e) => estadoDe(e) === 'Finalizada',
    );
    const transiciones = new Map<string, number>();
    const nodos = new Map<string, string>();
    for (const e of finalizadas) {
      const apertura = presentacionPrioridad(e.prioridadApertura);
      const cierre = presentacionPrioridad(
        e.prioridadCierre || e.prioridadApertura,
      );
      const desde = `${apertura.label} (apertura)`;
      const hacia = `${cierre.label} (cierre)`;
      nodos.set(desde, apertura.color);
      nodos.set(hacia, cierre.color);
      const k = `${desde}|||${hacia}`;
      transiciones.set(k, (transiciones.get(k) ?? 0) + 1);
    }
    const data = [...transiciones.entries()].map(([k, n]) => {
      const [desde, hacia] = k.split('|||');
      return [desde, hacia, n] as [string, string, number];
    });

    this.haySankey = data.length > 0;
    this.opcionesSankey = {
      chart: {
        type: 'sankey',
        height: 360,
        backgroundColor: 'var(--chart-background-color, #ffffff)',
        style: { color: 'var(--fg-grafico)' },
      },
      // El título va en el encabezado del panel
      title: { text: '' },
      legend: { enabled: false },
      tooltip: {
        headerFormat: '',
        pointFormat:
          '<b>{point.fromNode.name} → {point.toNode.name}</b>: {point.weight}',
      },
      plotOptions: {},
      series: [
        {
          type: 'sankey',
          name: 'Emergencias',
          keys: ['from', 'to', 'weight'],
          data,
          nodes: [...nodos.entries()].map(([id, color]) => ({ id, color })),
          tooltip: { nodeFormat: '{point.name}: <b>{point.sum}</b>' },
          dataLabels: {
            style: {
              color: 'var(--fg-grafico)',
              textOutline: 'none',
              fontWeight: 'normal',
            },
          },
        } as Highcharts.SeriesSankeyOptions,
      ],
    };
    // app-graficos no regenera ante cambios de `opciones`: se recrea
    this.versionSankey++;
  }

  private procesarRankings(): void {
    if (this.cargando.historicas && !this.historicas.length) return;

    const nombreAmbulancia = new Map(
      this.ambulancias.map((a) => [a._id, a.identificacion]),
    );
    const nombrePersonal = new Map(this.personal.map((p) => [p._id, p.nombre]));
    const nombreHospital = new Map(
      this.hospitales.map((h) => [h._id, h.identificacion]),
    );

    const ambulancias = new Map<string, number>();
    const personal = new Map<string, number>();
    const hospitales = new Map<string, number>();
    const sumar = (m: Map<string, number>, id?: string) => {
      if (id) m.set(id, (m.get(id) ?? 0) + 1);
    };

    for (const e of this.historicas) {
      const d = e.ultimoEventoEmergencia?.detallesEmergencias;
      if (!d) continue;
      sumar(ambulancias, d.idMovilAsignado);
      for (const id of [...(d.idsMedicos ?? []), ...(d.idsEnfermeros ?? [])]) {
        sumar(personal, id);
      }
      sumar(hospitales, d.idHospital);
    }

    const top = (
      m: Map<string, number>,
      nombres: Map<string | undefined, string | undefined>,
      sinNombre: string,
      color: string,
    ) => {
      const filas = [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
      return {
        categorias: filas.map(([id]) => nombres.get(id) || sinNombre),
        series: filas.length
          ? [{ name: 'Emergencias', data: filas.map(([, n]) => n), color }]
          : [],
      };
    };

    this.rankings = {
      rankingAmbulancias: top(
        ambulancias,
        nombreAmbulancia,
        'Ambulancia sin identificar',
        presentacionEstado('En tránsito').color,
      ),
      rankingPersonal: top(
        personal,
        nombrePersonal,
        'Personal sin identificar',
        presentacionSituacion('Llamada').color,
      ),
      rankingHospitales: top(
        hospitales,
        nombreHospital,
        'Hospital sin identificar',
        presentacionEstado('Llegada a hospital').color,
      ),
    };
  }

  private procesarMapa(): void {
    const puntos = this.historicas
      .filter((e) => e.esAuxilio)
      .map((e) => e.ubicacionDestino?.coordinates)
      .filter(
        (c): c is [number, number] =>
          Array.isArray(c) && isFinite(c[0]) && isFinite(c[1]),
      );

    if (!puntos.length) {
      this.mapaHistorico = undefined;
      this.subtituloMapa = '';
      return;
    }
    this.subtituloMapa = `${numero(puntos.length)} auxilios del período`;
    const centro: [number, number] = [
      puntos.reduce((a, c) => a + c[0], 0) / puntos.length,
      puntos.reduce((a, c) => a + c[1], 0) / puntos.length,
    ];
    // Sin título: el marco y el encabezado los pone el panel
    this.mapaHistorico = {
      showShadow: false,
      height: '360px',
      nombreMap: 'tableroEmergenciasMedicasHeatmap',
      mapOptions: {
        heatmaps: [{ points: puntos, radius: 20 }],
        center: centro,
        zoom: 12,
      },
    };
  }

  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.inicializarGraficos();
    this.actualizarStatCards();
    this.listarActivas();
    this.listarEntidades();
    this.listarHistoricas();
  }

  ngOnDestroy(): void {
    this.activas$?.unsubscribe();
    this.ambulancias$?.unsubscribe();
    this.personal$?.unsubscribe();
    this.hospitales$?.unsubscribe();
  }
}
